




















































































































































































































































































































import Popper from "popper.js";
import { Action, namespace } from "vuex-class";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Draggable from "vuedraggable";

import { excludes } from "@/codes";
import { isOutOfView } from "@/utils";
import { Code, Evidence } from "@/types";
import { Settings } from "@/store/settings";
import Tooltip from "@/components/Tooltip.vue";
import CodeList from "@/components/CodeList.vue";

const { isEqual } = require("lodash");
const Details = namespace("details");
const Evidences = namespace("evidences");
const SettingsStore = namespace("settings");

@Component({
  components: { CodeList, Draggable, Tooltip }
})
export default class EvidencesTable extends Vue {
  private popper: Popper | null = null;

  private navigated: Evidence | null = null;

  private duplicates: { [code: string]: string[] } = {};

  private timeout?: number;

  private hovered: Evidence | null = null;

  private extended: Evidence | null = null;

  @Prop({ type: Boolean, default: true })
  private showCode!: boolean;

  @Prop({ type: Boolean, default: false })
  private editable!: boolean;

  @Details.State("evidence")
  private selected!: Evidence;

  @Prop()
  private evidences!: Evidence[];

  @Prop({ type: Boolean, default: true })
  private draggable!: boolean;

  @Evidences.State("inspected")
  private inspected!: Set<string>;

  @Evidences.Getter("indexed")
  private index!: Record<string, Evidence>;

  @Evidences.Getter("sorted")
  private sorted!: Evidence[];

  @SettingsStore.State("current")
  private settings!: Settings;

  @Evidences.Action("remove")
  private remove!: (code: string) => void;

  @Evidences.Action("replace")
  private replace!: (params: { original: string; new: Code }) => void;

  @Evidences.Action("mutate")
  private mutate!: (list: Evidence[]) => void;

  @Action("details/update")
  select!: (value: Evidence | null) => void;

  @Prop({ type: Boolean, default: false })
  private showStatistics!: boolean;

  removeAndEmit(code: string) {
    const evidence = this.evidences.find(evidence => evidence.id === code);
    this.remove(code);
    this.$emit("removed", evidence);
  }

  navigate(event: Event, evidence: Evidence) {
    this.cancelHide();
    this.navigated = evidence;

    if (event.target instanceof Element && this.$refs.popover instanceof Vue) {
      this.select(null);

      if (this.popper) {
        this.popper.reference = event.target;
      } else {
        this.popper = new Popper(
          event.target,
          (this.$refs.popover as Vue).$el,
          {
            placement: "left",
            modifiers: {
              flip: { enabled: false },
              preventOverflow: {
                escapeWithReference: true
              },
              computeStyle: {
                gpuAcceleration: false
              }
            }
          }
        );
      }

      this.update();
    }
  }

  update() {
    this.$nextTick(() => {
      if (this.popper) this.popper.update();
    });
  }

  hide(timeout: number = 500) {
    this.timeout = window.setTimeout(() => {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    }, timeout);
  }

  cancelHide() {
    if (this.timeout) window.clearTimeout(this.timeout);
  }

  @Watch("sorted")
  clusterizeCodes() {
    this.duplicates = Object.assign(
      {},
      ...this.sorted
        .reduce(
          (
            clusters: string[][],
            evidence: Evidence,
            index: number,
            arr: Evidence[]
          ) => {
            if (
              clusters.length === 0 ||
              arr[index].spans.length === 0 ||
              arr[index - 1].spans.length === 0 ||
              !isEqual(arr[index].spans, arr[index - 1].spans) ||
              arr[index].score !== arr[index - 1].score
            ) {
              clusters.push([evidence.id]);
            } else {
              clusters[clusters.length - 1].push(evidence.id);
            }

            return clusters;
          },
          []
        )
        .filter((cluster: string[]) => cluster.length > 1)
        .map((cluster: string[]) =>
          Object.assign(
            {},
            ...cluster.map((code: string) => ({ [code]: cluster }))
          )
        )
    );
  }

  mounted() {
    this.clusterizeCodes();
  }

  getExcluded(code: Code) {
    return excludes[code.id] || [];
  }

  isExcluded(code: Code) {
    return this.getExcluded(code).filter(value =>
      [
        ...this.evidences.map(evidence => evidence.id),
        ...this.evidences
          .map(evidence => evidence.better_matches)
          .filter(Boolean)
          .flat(1)
      ].includes(value)
    );
  }

  @Watch("selected")
  onSelectedChanged() {
    if (this.selected) {
      if (
        !(
          this.evidences
            .map(evidence => evidence.id)
            .includes(this.selected.id) ||
          (this.extended &&
            this.extended.better_matches &&
            this.extended.better_matches.includes(this.selected.id))
        )
      ) {
        for (let evidence of this.evidences) {
          if (
            evidence.better_matches &&
            evidence.better_matches.includes(this.selected.id)
          ) {
            this.extended = evidence;
            break;
          }
        }
      }

      this.scrollTo(this.selected.id);
    }
  }

  scrollTo(code: string) {
    const elements = this.$refs[`code-${code}`] as HTMLElement[];
    const container = this.$refs.container as HTMLElement;

    if (elements && container && isOutOfView(container, elements[0])) {
      container.scrollTop =
        elements[0].offsetTop -
        container.clientHeight / 2 +
        elements[0].clientHeight / 2;
    }
  }

  markAsBetterMatch(from: string, to: string) {
    for (let bm of this.index[from].better_matches || []) {
      if (
        bm !== to &&
        Object.values(this.index)
          .map(e => e.better_matches)
          .flat(1)
          .filter(Boolean)
          .filter(e => e === bm).length <= 1
      ) {
        this.remove(bm);
      }
    }

    this.remove(from);

    this.index[to].type = "verified";
  }
}
